import React from 'react';
import { UserPlus, UserMinus, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  itemName?: string;
  type?: 'onboarding' | 'offboarding' | 'movement';
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  type
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getTypeColor = () => {
    switch (type) {
      case 'onboarding':
        return 'text-blue-600';
      case 'offboarding':
        return 'text-red-600';
      case 'movement':
        return 'text-purple-600';
      default:
        return 'text-red-600';
    }
  };

  const getTypeIcon = () => {
    switch (type) {
      case 'onboarding':
        return UserPlus;
      case 'offboarding':
        return UserMinus;
      case 'movement':
        return ArrowRightLeft;
      default:
        return AlertTriangle;
    }
  };

  const Icon = getTypeIcon();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center ${getTypeColor()}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 mb-4">{message}</p>
              {itemName && (
                <div className="bg-muted border border-border rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-gray-900">{itemName}</p>
                </div>
              )}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800">
                  {t('This action cannot be undone. All associated data will be permanently removed.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            {t('Cancel')}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            {t('Delete Permanently')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
